import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/lib/reveal";
import { SectionMarker } from "@/components/SectionMarker";
import { ROLE_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldBase =
  "w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3.5 text-white placeholder:text-slatemuted focus:border-teal transition-colors";

export const RegisterInterest = () => {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "", updates: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => {
    const val = key === "updates" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(form.email)) next.email = "Please enter a valid email address.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/interest`, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role || null,
        message: form.message.trim() || null,
        updates_opt_in: form.updates,
      });
      setDone(true);
      toast.success("Thank you — your interest has been registered.");
      setForm({ name: "", email: "", role: "", message: "", updates: false });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 lg:py-32" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <SectionMarker n="06" label="Early access" />
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              Help shape a future where accessibility information is easier to share.
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Interested in Aiga Enable or A-ID? Stay connected as we continue developing the platform.
            </p>
            <p className="mt-6 text-base text-slatemuted">
              Prefer email?{" "}
              <a
                href="mailto:support@aigaenable.co.nz"
                className="font-medium text-teal hover:text-cyan underline underline-offset-4"
                data-testid="contact-email-link"
              >
                support@aigaenable.co.nz
              </a>
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl glass p-6 sm:p-8">
              {done ? (
                <div className="flex flex-col items-center text-center py-10" data-testid="register-success">
                  <CheckCircle2 className="h-14 w-14 text-teal" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-2xl font-bold text-white">You’re on the list.</h3>
                  <p className="mt-2 text-slate-300">
                    Thank you for registering your interest. We’ll be in touch as A-ID develops.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="mt-6 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                    data-testid="register-again-btn"
                  >
                    Register another interest
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate data-testid="register-form">
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="ri-name" className="block text-sm font-medium text-slate-200 mb-2">
                        Name <span className="text-teal" aria-hidden="true">*</span>
                      </label>
                      <input
                        id="ri-name"
                        type="text"
                        autoComplete="name"
                        value={form.name}
                        onChange={update("name")}
                        className={fieldBase}
                        placeholder="Your full name"
                        aria-required="true"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "ri-name-err" : undefined}
                        data-testid="register-name-input"
                      />
                      {errors.name && (
                        <p id="ri-name-err" className="mt-1.5 text-sm text-red-400" data-testid="register-name-error">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="ri-email" className="block text-sm font-medium text-slate-200 mb-2">
                        Email <span className="text-teal" aria-hidden="true">*</span>
                      </label>
                      <input
                        id="ri-email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={update("email")}
                        className={fieldBase}
                        placeholder="you@example.com"
                        aria-required="true"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "ri-email-err" : undefined}
                        data-testid="register-email-input"
                      />
                      {errors.email && (
                        <p id="ri-email-err" className="mt-1.5 text-sm text-red-400" data-testid="register-email-error">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="ri-role" className="block text-sm font-medium text-slate-200 mb-2">
                        I am a <span className="text-slatemuted font-normal">(optional)</span>
                      </label>
                      <select
                        id="ri-role"
                        value={form.role}
                        onChange={update("role")}
                        className={`${fieldBase} appearance-none cursor-pointer`}
                        data-testid="register-role-select"
                      >
                        <option value="" className="bg-navy-800">Select an option</option>
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r} className="bg-navy-800">{r}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="ri-message" className="block text-sm font-medium text-slate-200 mb-2">
                        Message <span className="text-slatemuted font-normal">(optional)</span>
                      </label>
                      <textarea
                        id="ri-message"
                        rows={4}
                        value={form.message}
                        onChange={update("message")}
                        className={`${fieldBase} resize-none`}
                        placeholder="Anything you’d like to share"
                        data-testid="register-message-input"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        id="ri-updates"
                        type="checkbox"
                        checked={form.updates}
                        onChange={update("updates")}
                        className="mt-1 h-5 w-5 rounded border-white/20 bg-white/5 text-teal focus:ring-teal cursor-pointer"
                        data-testid="register-updates-checkbox"
                      />
                      <label htmlFor="ri-updates" className="text-sm text-slate-300 cursor-pointer">
                        Keep me updated on Aiga Enable and A-ID.
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-7 py-4 text-base font-semibold text-navy-900 hover:bg-cyan transition-colors disabled:opacity-70 disabled:cursor-not-allowed shadow-[0_0_30px_-6px_rgba(0,240,255,0.6)]"
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Registering…
                        </>
                      ) : (
                        <>
                          Register Your Interest
                          <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
